import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { LoginFlow, UiNodeInputAttributes, UpdateLoginFlowBody } from '@ory/client';
import { AxiosError } from 'axios';
import { getOrySdk } from '../../services/ory';
import { handleFlowError } from '../../services/oryErrors';
import { useAuth } from '../../context';
import { SocialLoginButtons } from '../../components/auth/SocialLoginButtons';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { syncSession, isAuthenticated } = useAuth();

  const [flow, setFlow] = useState<LoginFlow | undefined>();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  // Get flow params from URL
  const flowId = searchParams.get('flow');
  const returnTo = searchParams.get('return_to');
  const refresh = searchParams.get('refresh');
  const aal = searchParams.get('aal');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !refresh && !aal) {
      if (returnTo && returnTo.startsWith('http')) {
        window.location.href = returnTo;
      } else {
        navigate(returnTo || '/', { replace: true });
      }
    }
  }, [isAuthenticated, navigate, returnTo, refresh, aal]);

  // Initialize or fetch login flow
  useEffect(() => {
    // If we already have a flow, do nothing
    if (flow) {
      setIsLoading(false);
      return;
    }

    const sdk = getOrySdk();

    // If ?flow=... was in the URL, fetch it
    if (flowId) {
      sdk
        .getLoginFlow({ id: flowId })
        .then(({ data }) => {
          setFlow(data);
          extractCsrfAndDefaults(data);
        })
        .catch(handleFlowError(navigate, 'login', setFlow, setError))
        .catch((err) => {
          setError(err.message || 'Failed to load login flow');
        })
        .finally(() => setIsLoading(false));
      return;
    }

    // Otherwise create a new flow
    sdk
      .createBrowserLoginFlow({
        refresh: Boolean(refresh),
        aal: aal ? String(aal) : undefined,
        returnTo: returnTo ? String(returnTo) : undefined,
      })
      .then(({ data }) => {
        setFlow(data);
        extractCsrfAndDefaults(data);
        // Update URL with flow ID (without navigation)
        window.history.replaceState(null, '', `/login?flow=${data.id}`);
      })
      .catch(handleFlowError(navigate, 'login', setFlow, setError))
      .catch((err) => {
        setError(err.message || 'Failed to initialize login');
      })
      .finally(() => setIsLoading(false));
  }, [flowId, flow, navigate, returnTo, refresh, aal]);

  // Extract CSRF token, default values, and info messages from flow
  const extractCsrfAndDefaults = (flowData: LoginFlow) => {
    flowData.ui.nodes?.forEach((node) => {
      if (node.type === 'input') {
        const attrs = node.attributes as UiNodeInputAttributes;
        // Pre-fill identifier if available
        if (attrs.name === 'identifier' && attrs.value) {
          setEmail(String(attrs.value));
        }
      }
    });

    // Check for info messages (e.g., account linking messages from OIDC)
    const infoMessages: string[] = [];
    flowData.ui.messages?.forEach((m) => {
      if (m.type === 'info') {
        infoMessages.push(m.text);
      }
    });

    // Check if this is an account linking flow (OIDC tried to link to existing account)
    // Kratos sends specific message IDs for this scenario
    const isLinkingFlow = flowData.ui.messages?.some(
      (m) => m.id === 4000006 || // "An account with the same identifier exists already"
             m.text?.toLowerCase().includes('link') ||
             m.text?.toLowerCase().includes('already exists')
    );

    if (isLinkingFlow) {
      setInfoMessage(
        'An account with this email already exists. Please sign in with your existing credentials to link your social account.'
      );
    } else if (infoMessages.length > 0) {
      setInfoMessage(infoMessages.join(' '));
    } else {
      setInfoMessage(null);
    }
  };

  // Get CSRF token from flow
  const getCsrfToken = useCallback((): string => {
    let csrf = '';
    flow?.ui.nodes?.forEach((node) => {
      if (node.type === 'input') {
        const attrs = node.attributes as UiNodeInputAttributes;
        if (attrs.name === 'csrf_token' && attrs.value) {
          csrf = String(attrs.value);
        }
      }
    });
    return csrf;
  }, [flow]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flow) return;

    setIsSubmitting(true);
    setError(null);

    const sdk = getOrySdk();

    const isAal2 = flow.requested_aal === 'aal2';

    const body: UpdateLoginFlowBody = isAal2
      ? {
          method: 'totp',
          totp_code: totpCode,
          csrf_token: getCsrfToken(),
        }
      : {
          method: 'password',
          identifier: email,
          password: password,
          csrf_token: getCsrfToken(),
        };

    try {
      await sdk.updateLoginFlow({
        flow: flow.id,
        updateLoginFlowBody: body,
      });

      // Login successful - sync session and redirect
      await syncSession();

      if (flow.return_to) {
        window.location.href = flow.return_to;
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      const axiosErr = err as AxiosError<any>;

      // Handle flow errors (expired, csrf, etc.)
      try {
        await handleFlowError(navigate, 'login', setFlow, setError)(axiosErr);
      } catch (unhandledErr) {
        // Form validation error - update flow with error messages
        if (axiosErr.response?.status === 400) {
          setFlow(axiosErr.response.data);
          extractErrorMessages(axiosErr.response.data);
        } else {
          setError(axiosErr.message || 'Login failed. Please try again.');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Extract error messages from flow UI
  const extractErrorMessages = (flowData: LoginFlow) => {
    const messages: string[] = [];

    // Global messages
    flowData.ui.messages?.forEach((m) => {
      if (m.type === 'error') {
        messages.push(m.text);
      }
    });

    // Node-level messages
    flowData.ui.nodes?.forEach((node) => {
      node.messages?.forEach((m) => {
        if (m.type === 'error') {
          messages.push(m.text);
        }
      });
    });

    if (messages.length > 0) {
      setError(messages.join(' '));
    } else {
      setError('Invalid credentials. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="xs">
        <Paper sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img
              src="/logo.png"
              alt="FamilyPal"
              style={{ width: 120, height: 'auto', marginBottom: 16 }}
            />
            <Typography variant="h4" component="h1" fontWeight={700} color="primary">
              FamilyPal
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {flow?.refresh
                ? 'Confirm your identity'
                : flow?.requested_aal === 'aal2'
                  ? 'Two-factor authentication'
                  : infoMessage
                    ? 'Link your account'
                    : 'Sign in to continue'}
            </Typography>
          </Box>

          {infoMessage && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {infoMessage}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {flow?.requested_aal === 'aal2' ? (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Verification Code"
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                margin="normal"
                required
                autoComplete="one-time-code"
                autoFocus
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting || !totpCode}
                sx={{ mt: 3, mb: 2 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Verify'}
              </Button>
            </form>
          ) : (
            <>
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="email"
                  autoFocus
                />
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSubmitting || !email || !password}
                  sx={{ mt: 3, mb: 2 }}
                >
                  {isSubmitting ? <CircularProgress size={24} /> : 'Sign In'}
                </Button>
              </form>

              <SocialLoginButtons flow={flow} disabled={isSubmitting} />
            </>
          )}

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link component={RouterLink} to="/recovery" variant="body2">
              Forgot password?
            </Link>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Don't have an account?{' '}
              <Link component={RouterLink} to="/signup">
                Sign up
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;
